import { useState, useEffect, useRef } from 'react'
import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../services/network'
import { useSnackBar } from '../providers/snack_bar_provider'
import { ChatRoom } from '../clients/kyousou_api_client'

interface UseChatResult {
  chats: ChatRoom[]
  firebaseToken: string | null
  loading: boolean
  error: string | null
  refetch: () => void
  markAsRead: (chatRoomId: number) => Promise<void>
}

export const useChat = (): UseChatResult => {
  const [chats, setChats] = useState<ChatRoom[]>([])
  const [firebaseToken, setFirebaseToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showSnackBar } = useSnackBar()
  const apiClient = getKyousouApiClient()
  const markAsReadInProgressRef = useRef<Set<number>>(new Set())

  const fetchChats = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.chat
        .roomsList()
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          return null
        })

      if (response) {
        setChats(response.data.rooms || [])
        setFirebaseToken(response.data.token || null)
      }
    } catch (err) {
      setError('チャットデータの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChats()
  }, [])

  const refetch = () => {
    fetchChats()
  }

  const markAsRead = async (chatRoomId: number) => {
    // 既に処理中の場合はスキップ
    if (markAsReadInProgressRef.current.has(chatRoomId)) {
      return
    }

    // 処理中フラグを立てる
    markAsReadInProgressRef.current.add(chatRoomId)

    try {
      await apiClient.chat.roomsReadCreate(chatRoomId)
    } catch (e) {
      handleError(e as ApiErrorResponse, showSnackBar)
    } finally {
      // 処理完了後、フラグを解除（500ms後）
      setTimeout(() => {
        markAsReadInProgressRef.current.delete(chatRoomId)
      }, 500)
    }
  }

  return {
    chats,
    firebaseToken,
    loading,
    error,
    refetch,
    markAsRead,
  }
}
